import { Check, ChevronDown, LoaderCircle, Search, SlidersHorizontal } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { groupOptions, isGrouped } from "@/shared/components/group-options";
import { filterBySearch } from "@/shared/utils/text-search";

/**
 * Um filtro de multi-seleção com modo incluir/excluir.
 *
 * Nasceu inline no dashboard e virou compartilhado em 2026-08-27, quando
 * a tela de menções adotou o mesmo modelo (pedido da dona: um modelo de
 * filtro só na plataforma inteira). As duas telas fazem a MESMA pergunta
 * — "estes valores, ou tudo menos estes" — e a resposta vem da mesma
 * função no servidor (`core/database/facets.py`); o controle ser o mesmo
 * é a metade visual dessa promessa.
 *
 * **As classes são `multi-filter-*`, não `dashboard-*`.** Não é
 * cosmética: o drawer de menções tem regras próprias
 * (`.drawer label`, `.drawer input` em `pages.css`) que vencem por
 * especificidade e escopo, e a mais visível é o campo de busca — o
 * padding do drawer é menor que o do dashboard, e o ícone da lupa, que é
 * posicionado por cima, cobria o texto digitado. Classe própria põe as
 * duas telas na mesma régua.
 *
 * Busca só aparece a partir de `SEARCH_THRESHOLD` opções: abaixo disso
 * ela ocupa espaço sem economizar rolagem.
 *
 * **Busca no servidor (2026-10-19)**: com a prop `search` a caixa de
 * busca aparece SEMPRE e digitar pergunta ao servidor, na mecânica da
 * `SearchSelect` (300 ms depois da última tecla, descarte por carga: a
 * resposta de "sa" que chega depois da de "sao" é jogada fora). É pra
 * catálogo que não cabe em memória — ~14 mil veículos numa empresa do
 * legado. Sem texto, a caixa mostra `options`. As opções MARCADAS ficam
 * sempre no topo, mesmo fora do resultado: senão a pessoa não teria
 * como desmarcar o que escolheu numa busca anterior.
 */

const SEARCH_THRESHOLD = 8;
const SEARCH_DELAY_MS = 300;

export type MultiSelectOption<T extends string> = {
	value: T;
	label: string;
	/** Título de grupo sob o qual a opção aparece (tema -> grupo de tema).
	 *  O MESMO `value` pode vir em mais de uma entrada, cada uma com um
	 *  `group`: no filtro o tema é por NOME (2026-08-27: homônimos entre
	 *  marcas são uma opção só), e o mesmo nome pode estar em grupos
	 *  diferentes. `selected` é por `value`, então marcar num lugar marca
	 *  no outro. Nenhuma opção com grupo = lista plana, como sempre. */
	group?: string;
	/** Opção fixa no TOPO, fora de qualquer grupo e sem título ("Sem
	 *  subtema"): numa lista agrupada, uma opção sem grupo cairia no
	 *  bloco "Sem tema" do fim. A busca ainda a filtra pelo rótulo. */
	top?: boolean;
};

export function MultiSelectFilter<T extends string>({
	label,
	hint,
	unavailable,
	options,
	search: searchOnServer,
	selected,
	exclude,
	onExcludeChange,
	onToggle,
	onClear,
	closeOnOutsideClick = false,
	alwaysSearch = false,
	excludable = true
}: {
	label: string;
	/** Aviso de escopo: dimensão que só existe em metade das menções
	 *  (tier e formato são de imprensa; porte e grupo de publicador são
	 *  de rede). Desde 2026-08-27 o filtro recorta só essa metade e
	 *  deixa a outra passar — o aviso conta isso. */
	hint?: string;
	/** Por que esta dimensão não existe NESTE contexto. Quando presente,
	 *  o filtro aparece desabilitado com o motivo em vez de sumir —
	 *  esconder faria a pessoa procurar um controle que ela viu em outro
	 *  lugar sem nunca saber por que ele não está ali. */
	unavailable?: string;
	options: Array<MultiSelectOption<T>>;
	/** Busca no servidor; ausente, a caixa filtra `options` localmente
	 *  (e só aparece a partir de `SEARCH_THRESHOLD`). */
	search?: (query: string) => Promise<Array<MultiSelectOption<T>>>;
	selected: Set<T>;
	exclude: boolean;
	onExcludeChange: (value: boolean) => void;
	onToggle: (value: T) => void;
	/** Fecha a lista ao clicar fora dela (ou com Esc). Pra lista que
	 *  FLUTUA sobre o conteúdo (os filtros do dashboard, 2026-10-25): numa
	 *  gaveta, onde a lista abre no fluxo como acordeão, o clique fora é
	 *  a pessoa abrindo a seção vizinha, e fechar tiraria o que ela vê. */
	closeOnOutsideClick?: boolean;
	/** A caixa de busca aparece sempre, com qualquer número de opções, e
	 *  ganha o foco ao abrir (2026-10-30: a barra de filtros rápidos de
	 *  Menções — quem abre um filtro ali já quer procurar). */
	alwaysSearch?: boolean;
	/** `false` esconde o modo Incluir/Excluir (2026-10-31): dimensão de
	 *  inclusão pura, como a Clipadora, onde o servidor não tem como
	 *  excluir. Com o modo escondido `exclude` e `onExcludeChange` são
	 *  ignorados. */
	excludable?: boolean;
	onClear: () => void;
}) {
	const [search, setSearch] = useState("");
	const [results, setResults] = useState<Array<MultiSelectOption<T>> | null>(null);
	const [searching, setSearching] = useState(false);
	const [failed, setFailed] = useState(false);
	const loadRef = useRef(0);
	// Rótulo de todo valor já visto (opções e resultados): é o que desenha
	// o escolhido que saiu do resultado da busca.
	const seenRef = useRef(new Map<T, string>());
	const slug = label.toLocaleLowerCase("pt-BR").replaceAll(" ", "-");
	const detailsRef = useRef<HTMLDetailsElement>(null);

	useEffect(() => {
		if (!closeOnOutsideClick) return;
		const closeOutside = (event: PointerEvent) => {
			const details = detailsRef.current;
			if (details?.open && event.target instanceof Node && !details.contains(event.target)) details.open = false;
		};
		const closeOnEscape = (event: KeyboardEvent) => {
			const details = detailsRef.current;
			if (event.key !== "Escape" || !details?.open || !details.contains(document.activeElement)) return;
			details.open = false;
			details.querySelector("summary")?.focus();
		};
		document.addEventListener("pointerdown", closeOutside);
		document.addEventListener("keydown", closeOnEscape);
		return () => {
			document.removeEventListener("pointerdown", closeOutside);
			document.removeEventListener("keydown", closeOnEscape);
		};
	}, [closeOnOutsideClick]);
	const query = search.trim();

	useEffect(() => {
		if (!searchOnServer || !query) {
			setResults(null);
			setSearching(false);
			return;
		}
		const load = ++loadRef.current;
		setSearching(true);
		const timer = setTimeout(() => {
			searchOnServer(query)
				.then(found => {
					if (loadRef.current !== load) return;
					setResults(found);
					setFailed(false);
				})
				.catch(() => {
					if (loadRef.current !== load) return;
					setResults([]);
					setFailed(true);
				})
				.finally(() => {
					if (loadRef.current === load) setSearching(false);
				});
		}, SEARCH_DELAY_MS);
		return () => clearTimeout(timer);
	}, [searchOnServer, query]);

	for (const option of options) seenRef.current.set(option.value, option.label);
	for (const option of results ?? []) seenRef.current.set(option.value, option.label);
	const base = searchOnServer ? (results ?? options) : options;
	// Marcadas fora da lista mostrada ficam no topo (só com busca no
	// servidor: a lista local já contém tudo o que dá pra marcar).
	const pinned: Array<MultiSelectOption<T>> = searchOnServer
		? [...selected]
			.filter(value => !base.some(option => option.value === value))
			.map(value => ({ value, label: seenRef.current.get(value) ?? String(value) }))
		: [];
	const shownOptions: Array<MultiSelectOption<T>> = [...pinned, ...base];
	const topOptions = shownOptions.filter(option => option.top);
	const listedOptions = shownOptions.filter(option => !option.top);
	const grouped = isGrouped(listedOptions, option => option.group);
	const term = searchOnServer ? "" : search;
	const topBlock = filterBySearch(topOptions, term, option => [option.label]);
	const blocks: Array<{ group: string | null; options: Array<MultiSelectOption<T>>; top?: boolean }> = [
		...(topBlock.length ? [{ group: null, options: topBlock, top: true }] : []),
		...groupOptions(listedOptions, term, {
			labelOf: option => option.label,
			groupOf: option => option.group
		})
	];

	// "Marcar todas" age sobre o que está À VISTA: com busca digitada, só o
	// que casa (local ou trazido pelo servidor); sem busca, tudo. O mesmo
	// `value` sob dois grupos conta uma vez, e só entra quem ainda não está
	// marcado — marcar tudo nunca desmarca nada.
	const unmarkedVisible = [...new Set(blocks.flatMap(block => block.options.map(option => option.value)))]
		.filter(value => !selected.has(value));

	if (unavailable) {
		return (
			<details className="multi-filter is-unavailable">
				<summary>
					<SlidersHorizontal size={15} aria-hidden="true"/>
					<span>{label}</span>
					<small>Indisponível</small>
					<ChevronDown size={16} aria-hidden="true"/>
				</summary>
				<div className="multi-filter-body">
					<p className="multi-filter-hint">{unavailable}</p>
				</div>
			</details>
		);
	}

	return (
		<details
			className="multi-filter"
			ref={detailsRef}
			onToggle={event => {
				if (!alwaysSearch || !event.currentTarget.open) return;
				event.currentTarget.querySelector<HTMLInputElement>(".multi-filter-search input")?.focus();
			}}
		>
			<summary>
				<SlidersHorizontal size={15} aria-hidden="true"/>
				<span>{label}</span>
				<small>
					{selected.size
						? `${selected.size} ${selected.size === 1 ? "selecionado" : "selecionados"}`
						: "Todos"}
				</small>
				<ChevronDown size={16} aria-hidden="true"/>
			</summary>
			<div className="multi-filter-body">
				{hint && <p className="multi-filter-hint">{hint}</p>}
				<div className="multi-filter-head">
					{excludable && (
						<div className="multi-filter-mode" role="group" aria-label={`Modo do filtro ${label}`}>
							<button type="button" className={!exclude ? "active" : ""} aria-pressed={!exclude} onClick={() => onExcludeChange(false)}>Incluir</button>
							<button type="button" className={exclude ? "active is-excluding" : ""} aria-pressed={exclude} onClick={() => onExcludeChange(true)}>Excluir</button>
						</div>
					)}
					{/* UM botão que alterna: dois lado a lado estouravam a largura da
					    caixa e cortavam o segundo. */}
					<span className="multi-filter-bulk">
						{unmarkedVisible.length ? (
							<button type="button" className="text-button" onClick={() => unmarkedVisible.forEach(value => onToggle(value))}>Marcar todas</button>
						) : (
							<button type="button" className="text-button" onClick={onClear} disabled={!selected.size}>Limpar seleção</button>
						)}
					</span>
				</div>
				{(searchOnServer || alwaysSearch || options.length > SEARCH_THRESHOLD) && (
					<div className="multi-filter-search">
						{searching ? <LoaderCircle size={15} className="spin" aria-hidden="true"/> : <Search size={15} aria-hidden="true"/>}
						<input
							type="search"
							name={`filter-search-${slug}`}
							value={search}
							onChange={event => setSearch(event.target.value)}
							placeholder={searchOnServer ? `Digite para buscar em ${label}…` : `Buscar em ${label}…`}
							aria-label={`Buscar opções de ${label}`}
							autoComplete="off"
							spellCheck={false}
						/>
					</div>
				)}
				{failed && <p className="multi-filter-hint" role="alert">Não foi possível buscar. Tente de novo.</p>}
				<div className="multi-filter-options filter-chip-group">
					{blocks.map(block => (
						<Fragment key={block.top ? "\u0001" : (block.group ?? "\u0000")}>
							{grouped && !block.top && (
								<h5 className="multi-filter-group-title">{block.group ?? "Sem tema"}</h5>
							)}
							{block.options.map(option => (
								<button
									type="button"
									/* A chave leva o grupo: o mesmo `value` pode aparecer sob
									   mais de um título. */
									key={`${option.group ?? ""}\u0000${option.value}`}
									className={`filter-chip ${selected.has(option.value) ? "active" : ""}`}
									aria-pressed={selected.has(option.value)}
									onClick={() => onToggle(option.value)}
								>
									{selected.has(option.value) && <Check size={13} aria-hidden="true"/>}
									{option.label}
								</button>
							))}
						</Fragment>
					))}
					{!blocks.length && !searching && (
						<p className="multi-filter-empty">Nenhuma opção encontrada.</p>
					)}
				</div>
			</div>
		</details>
	);
}
